// ═══════════════════════════════════════════════════════════════
// CatalogIndexService — index du catalogue pour matching rapide
//
// Problème : à chaque appel de findCatalogSuggestions, on filtre 5000 items
// par catégorie, puis on recalcule formality/color pour chacun.
// Solution : indexer UNE FOIS par gender + pré-calculer les metadata.
//
// Gain : ~10-20× plus rapide sur le matching.
// ═══════════════════════════════════════════════════════════════

const CATEGORIES = ['Hauts', 'Bas', 'Robes', 'Vestes', 'Chaussures', 'Sacs', 'Bijoux', 'Accessoires'];

// ═══ SAISON DEPUIS TEMPÉRATURE ═══
// Retourne la saison "perçue" selon la température actuelle (pour le matching IA)
export function seasonFromTemp(tempC) {
  if (typeof tempC !== 'number' || isNaN(tempC)) return null;
  if (tempC < 5)  return 'hiver';
  if (tempC < 15) return 'automne';
  if (tempC < 25) return 'printemps';
  return 'été';
}

/**
 * Score de compatibilité météo entre un article et une température.
 * Utilise en priorité temperatureRange (IA) puis seasonality (fallback).
 *
 * @param {Object} item - produit avec ai_metadata.temperatureRange ou seasonality
 * @param {number} tempC - température actuelle en °C
 * @returns {number} 0-100 (100 = parfaitement adapté, 0 = incompatible)
 */
export function scoreWeatherMatch(item, tempC) {
  if (typeof tempC !== 'number' || isNaN(tempC)) return 50; // neutre

  const meta = item?.ai_metadata || {};

  // Priorité 1 : temperatureRange IA [min, max]
  const range = meta.temperatureRange;
  if (Array.isArray(range) && range.length === 2) {
    const [min, max] = range;
    if (typeof min === 'number' && typeof max === 'number') {
      if (tempC >= min && tempC <= max) return 100;      // dans la plage
      if (tempC >= min - 3 && tempC <= max + 3) return 75;  // légère marge (±3°)
      if (tempC >= min - 8 && tempC <= max + 8) return 40;  // marge large (±8°)
      return 10; // clairement hors plage
    }
  }

  // Priorité 2 : seasonality IA
  const seasons = meta.seasonality || [];
  if (Array.isArray(seasons) && seasons.length > 0) {
    const currentSeason = seasonFromTemp(tempC);
    if (!currentSeason) return 50;
    if (seasons.includes(currentSeason)) return 90;
    // Saisons adjacentes = OK
    const adjacentMap = {
      'hiver':    ['automne'],
      'automne':  ['hiver', 'printemps'],
      'printemps':['automne', 'été'],
      'été':      ['printemps'],
    };
    if ((adjacentMap[currentSeason] || []).some(s => seasons.includes(s))) return 60;
    return 20;
  }

  // Priorité 3 : fallback heuristique sur le nom + catégorie
  const name = (item?.name || '').toLowerCase();
  const category = item?.category || '';

  // Items hiver
  if (/pull|sweat|hoodie|cashmere|cachemire|laine|manteau|doudoune|parka|bonnet|gant/.test(name)) {
    if (tempC > 20) return 15;
    if (tempC > 15) return 40;
    return 95;
  }
  // Items été
  if (/short|débardeur|tank|top crop|sandale|bikini|maillot|lin|robe légère/.test(name)) {
    if (tempC < 15) return 15;
    if (tempC < 20) return 50;
    return 95;
  }
  // Item polyvalent (jean, t-shirt simple)
  if (/jean|t-shirt|tshirt|tee\b/.test(name)) return 70;

  // Catégorie Vestes + froid = bon match
  if (category === 'Vestes' && tempC < 18) return 85;
  if (category === 'Vestes' && tempC > 25) return 20;

  return 50; // neutre
}

// Cache : signature (catalogId + length) → index
let cachedIndex = null;
let cachedSignature = null;

/**
 * Calcule une formality rapide depuis les champs de l'item.
 * 1 (casual sportif) → 5 (cérémonie).
 */
function inferFormalityFast(item) {
  const name = (item.name || '').toLowerCase();
  const cat = item.category || '';

  // Bypass si ai_metadata présent
  if (item.ai_metadata?.formality) return item.ai_metadata.formality;

  // Catégorie baseline
  let base =
    cat === 'Robes' ? 3.5 :
    cat === 'Vestes' ? 3.5 :
    cat === 'Bijoux' ? 4 :
    cat === 'Sacs' ? 3 :
    3;

  // Ajustement par mots-clés
  if (/sport|run|training|basket|sneaker|trail|hoodie|jogging/.test(name)) base -= 1.5;
  if (/smok|tuxedo|evening|gala|blazer|suit|costume/.test(name)) base += 1.5;
  if (/soie|silk|satin|velvet|velours|cashmere/.test(name)) base += 0.5;
  if (/cotton|denim|jeans|tshirt|tee|polo/.test(name)) base -= 0.3;

  return Math.max(1, Math.min(5, base));
}

/**
 * Extraire la couleur primaire avec fallback sur le nom.
 */
function inferColorFast(item) {
  if (item.ai_metadata?.primaryColor) return item.ai_metadata.primaryColor;
  if (item.color) return item.color;

  const name = (item.name || '').toLowerCase();
  const colors = [
    ['noir', 'noir'], ['black', 'noir'],
    ['blanc', 'blanc'], ['white', 'blanc'],
    ['gris', 'gris'], ['grey', 'gris'], ['gray', 'gris'],
    ['bleu', 'bleu'], ['blue', 'bleu'], ['navy', 'bleu'],
    ['rouge', 'rouge'], ['red', 'rouge'], ['bordeaux', 'bordeaux'],
    ['vert', 'vert'], ['green', 'vert'], ['kaki', 'kaki'],
    ['rose', 'rose'], ['pink', 'rose'],
    ['beige', 'beige'], ['camel', 'camel'], ['cream', 'beige'],
    ['marron', 'marron'], ['brown', 'marron'],
    ['jaune', 'jaune'], ['yellow', 'jaune'],
    ['violet', 'violet'], ['purple', 'violet'],
  ];
  for (const [pat, color] of colors) {
    if (name.includes(pat)) return color;
  }
  return 'unknown';
}

/**
 * Construit l'index du catalogue.
 * Retourne : { byCategory: { Hauts: [...], Bas: [...] }, total: N }
 *
 * @param {Array} catalog - Liste des articles partenaires
 */
export function buildCatalogIndex(catalog) {
  if (!Array.isArray(catalog) || catalog.length === 0) {
    return { byCategory: {}, total: 0 };
  }

  // Cache : si la signature n'a pas changé, on réutilise
  const signature = `${catalog.length}_${catalog[0]?.id || ''}_${catalog[catalog.length - 1]?.id || ''}`;
  if (cachedSignature === signature && cachedIndex) {
    return cachedIndex;
  }

  const byCategory = {};
  for (const cat of CATEGORIES) byCategory[cat] = [];

  // Pré-calculer formality + color pour chaque item (économise des recalculs)
  for (const p of catalog) {
    if (!p || !p.category) continue;
    const enriched = {
      ...p,
      _formality: inferFormalityFast(p),
      _color: inferColorFast(p),
    };
    if (byCategory[p.category]) {
      byCategory[p.category].push(enriched);
    }
  }

  // Tri initial par qualité (CJ + promo d'abord)
  for (const cat of CATEGORIES) {
    byCategory[cat].sort((a, b) => {
      const aCj = (a.partner === 'cashmere' || a.partner === 'iqueens') ? 10 : 0;
      const bCj = (b.partner === 'cashmere' || b.partner === 'iqueens') ? 10 : 0;
      const aDisc = a.discount || 0;
      const bDisc = b.discount || 0;
      return (bCj + bDisc) - (aCj + aDisc);
    });
  }

  cachedIndex = { byCategory, total: catalog.length };
  cachedSignature = signature;
  return cachedIndex;
}

/**
 * Récupère rapidement les candidats d'une catégorie, limités au top N.
 * @param {Object} index - retourné par buildCatalogIndex
 * @param {string} category
 * @param {number} limit
 */
export function getCategoryCandidates(index, category, limit = 200) {
  if (!index || !index.byCategory) return [];
  const pool = index.byCategory[category] || [];
  return pool.slice(0, limit);
}

/**
 * Score rapide entre un item dressing et un candidat catalogue.
 * Plus simple que scoreColorCompat complet mais 5-10× plus rapide.
 */
export function fastMatchScore(dressingItem, candidateItem) {
  let score = 50;

  // 1. Match couleur (simple mais efficace)
  const cD = dressingItem._color || inferColorFast(dressingItem);
  const cC = candidateItem._color || inferColorFast(candidateItem);

  // Couleurs neutres vont avec tout
  const NEUTRAL = ['noir', 'blanc', 'gris', 'beige', 'camel'];
  if (NEUTRAL.includes(cC) || NEUTRAL.includes(cD)) {
    score += 30;
  } else if (cC === cD) {
    score += 20; // même couleur = OK mais pas top
  } else {
    // Combinaisons classiques
    const goodPairs = [
      ['bleu', 'beige'], ['bleu', 'blanc'], ['bleu', 'rouge'],
      ['rouge', 'noir'], ['rose', 'blanc'], ['vert', 'beige'],
      ['marron', 'beige'], ['kaki', 'beige'],
    ];
    for (const [a, b] of goodPairs) {
      if ((cD === a && cC === b) || (cD === b && cC === a)) {
        score += 25;
        break;
      }
    }
  }

  // 2. Formality match
  const fD = dressingItem._formality || inferFormalityFast(dressingItem);
  const fC = candidateItem._formality || inferFormalityFast(candidateItem);
  const diff = Math.abs(fD - fC);
  score += Math.max(0, 25 - diff * 10);

  // 3. Bonus CJ (affiliate)
  if (candidateItem.partner === 'cashmere' || candidateItem.partner === 'iqueens') {
    score += 15;
  }
  if ((candidateItem.discount || 0) > 20) score += 10;

  return score;
}

/**
 * Vide le cache d'index (à appeler lors d'un changement de gender).
 */
export function clearCatalogIndex() {
  cachedIndex = null;
  cachedSignature = null;
}

export { inferFormalityFast, inferColorFast };
